import { FC } from "react";

import { PageContext } from "@ryde/redux";

import { FilterMultiSelectReports } from "../../../reports/filters/parts/filter-multiselect.reports";
import { FiltersByIdsReport } from "../../../reports/filters/parts/filters-by-ids.report";
import { REPORT_FILTER_ENTITY } from "../dynamic-dashboard.constants";
import { IDashboardCardFiltersProps } from "./dashboard-card-filters.models";

/**
 * A card's own filters, rendered with the real report filter widgets — so a
 * card chip looks and behaves exactly like a filter on the reports page,
 * including the "[first item]+[n]" button label. The values are owned by the
 * caller (the editor's draft state, or the card in `dashboardCardsAtom`) rather
 * than by the reports page's shared filter store, which is what keeps a card's
 * filters independent of the dashboard-wide bar.
 *
 * Covers both widget families the reports page offers: list-backed (dropdown /
 * select list) and free text, so a card can pin every filter its report
 * declares.
 */
export const DashboardCardFilters: FC<IDashboardCardFiltersProps> = ({
  schema,
  values,
  singleValueFilterKeys,
  onValuesChange,
  onRemove,
}) => (
  <PageContext.Provider value={{ entity: REPORT_FILTER_ENTITY }}>
    {Object.entries(values).map(([templateKey, filterValues]) => {
      const { dropdownSource, selectItemsSource, filterFieldTypeId } =
        schema[templateKey] ?? {};

      if (dropdownSource || selectItemsSource) {
        return (
          <FilterMultiSelectReports
            key={templateKey}
            templateKey={templateKey}
            dropdownSource={dropdownSource}
            selectItemsSource={selectItemsSource}
            initialCheckedValues={filterValues}
            isSingleValue={singleValueFilterKeys?.includes(templateKey)}
            onValuesChange={onValuesChange}
            onRemove={onRemove}
          />
        );
      }

      return filterFieldTypeId ? (
        <FiltersByIdsReport
          key={templateKey}
          templateKey={templateKey}
          filterFieldTypeId={filterFieldTypeId}
          initialCheckedValues={filterValues}
          onValuesChange={onValuesChange}
          onRemove={onRemove}
        />
      ) : null;
    })}
  </PageContext.Provider>
);
